// Iter 86.19 / Phase E7 Phase C2 — Bulk CSV Onboarding tab.
//
// Two-stage UX:
//   Step 1: Drag-drop CSV (or paste link to template) → POST /validate
//           → Preview table with per-row classification (ok / warn_collision / error)
//   Step 2: If any warn_collision rows have empty action, operator picks
//           reactivate / view / create_new inline. Once all resolved → Confirm.
//   Step 3: POST /execute with mode (fail_fast / continue).
//           → Result modal with succeeded/failed counts + failures.csv download.
import { useRef, useState } from "react";
import {
    Upload, FileCsv, Warning, CheckCircle, XCircle, ArrowsClockwise,
    FloppyDisk, Download, ArrowLeft, Clock, ArrowsCounterClockwise,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";


function ClassPill({ classification }) {
    const map = {
        ok: { Icon: CheckCircle, text: "OK", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
        warn_collision: { Icon: Warning, text: "Archived collision", color: "bg-amber-50 text-amber-700 border-amber-200" },
        error: { Icon: XCircle, text: "Error", color: "bg-rose-50 text-rose-700 border-rose-200" },
    };
    const item = map[classification] || map.error;
    const { Icon, text, color } = item;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-full ${color}`}>
            <Icon className="w-3 h-3" /> {text}
        </span>
    );
}


function CollisionPicker({ row, onChange, disabled }) {
    const value = (row.raw?.archived_collision_action || "").toLowerCase();
    return (
        <select
            className="text-xs h-7 px-2 border border-slate-200 rounded-md bg-white"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(row.row_number, e.target.value)}
            data-testid={`collision-action-${row.row_number}`}
        >
            <option value="">— pick action —</option>
            <option value="reactivate">Reactivate archived</option>
            <option value="view">View archived (skip this row)</option>
            <option value="create_new">Create new employee anyway</option>
        </select>
    );
}


export default function BulkCSVTab() {
    const fileInputRef = useRef(null);
    const [file, setFile] = useState(null);
    const [validating, setValidating] = useState(false);
    const [batch, setBatch] = useState(null);
    const [executing, setExecuting] = useState(false);
    const [executeMode, setExecuteMode] = useState("fail_fast");
    const [result, setResult] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);

    const reset = () => {
        setFile(null);
        setBatch(null);
        setResult(null);
        setShowConfirm(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const downloadTemplate = async () => {
        try {
            const resp = await api.get("/admin/onboard/bulk-csv/template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = "bulk_onboarding_template.csv";
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Template downloaded");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const validate = async (chosenFile) => {
        if (!chosenFile) {
            toast.error("Pick a CSV file first");
            return;
        }
        setValidating(true);
        try {
            const fd = new FormData();
            fd.append("file", chosenFile);
            const resp = await api.post("/admin/onboard/bulk-csv/validate", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setBatch(resp.data);
            toast.success(
                `Validated ${resp.data.summary.total_rows} rows — ` +
                `${resp.data.summary.ok} ok, ${resp.data.summary.warn_collision} collision, ${resp.data.summary.error} error`
            );
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setValidating(false);
        }
    };

    const onCollisionChange = (rowNumber, action) => {
        setBatch((prev) => {
            if (!prev) return prev;
            const rows = prev.rows.map((r) =>
                r.row_number === rowNumber
                    ? { ...r, raw: { ...r.raw, archived_collision_action: action } }
                    : r
            );
            return { ...prev, rows };
        });
    };

    const unresolvedCollisions = batch
        ? batch.rows.filter(
            (r) =>
                r.classification === "warn_collision" &&
                !["reactivate", "view", "create_new"].includes(
                    (r.raw?.archived_collision_action || "").toLowerCase()
                )
        )
        : [];

    const execute = async () => {
        if (!batch) return;
        if (unresolvedCollisions.length > 0) {
            toast.error(`Pick an action for ${unresolvedCollisions.length} collision row(s) first`);
            return;
        }
        // For collision rows, we need to send the updated archived_collision_action
        // back to the server. The execute endpoint reads the batch from DB. Patch
        // the batch first via a re-validate would re-write the batch, easier:
        // we'll call execute with a new param flow. Since the backend reads the
        // batch from db, we have to PATCH the batch with the collision choices.
        try {
            await api.patch(`/admin/onboard/bulk-csv/batch/${batch.batch_id}/collision-actions`, {
                actions: batch.rows
                    .filter((r) => r.classification === "warn_collision")
                    .map((r) => ({
                        row_number: r.row_number,
                        action: (r.raw?.archived_collision_action || "").toLowerCase(),
                    })),
            });
        } catch (err) {
            toast.error("Failed to persist collision actions: " + formatApiError(err));
            return;
        }

        setExecuting(true);
        try {
            const resp = await api.post("/admin/onboard/bulk-csv/execute", {
                batch_id: batch.batch_id,
                mode: executeMode,
            });
            setResult(resp.data);
            setShowConfirm(false);
            toast.success(
                `Committed: ${resp.data.succeeded_count} succeeded, ${resp.data.failed_count} failed`
            );
        } catch (err) {
            // fail_fast halts produce 422 with a structured detail
            const detail = err?.response?.data?.detail;
            if (typeof detail === "object" && detail !== null) {
                setResult({
                    halted: true,
                    detail: detail.detail || JSON.stringify(detail),
                    rolled_back: detail.rolled_back,
                    succeeded_before_halt: detail.succeeded_before_halt || [],
                });
                setShowConfirm(false);
                toast.error("Halted — batch rolled back. See details below.");
            } else {
                toast.error(formatApiError(err));
            }
        } finally {
            setExecuting(false);
        }
    };

    const downloadFailures = async () => {
        if (!result?.failures_csv_url) return;
        // failures_csv_url comes back as "/api/admin/onboard/bulk-csv/batch/{id}/failures.csv".
        // window.open won't include the bearer token, so we fetch with auth and
        // synthesise a download via a Blob URL.
        try {
            const apiPath = result.failures_csv_url.replace(/^\/api/, "");
            const resp = await api.get(apiPath, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `bulk_failures_${batch?.batch_id || "batch"}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("failures.csv downloaded");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    // ── Render ────────────────────────────────────────────────────────
    return (
        <div className="space-y-6" data-testid="bulk-csv-tab">
            {/* Step 1 — Upload + template */}
            {!batch && !result && (
                <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-5" data-testid="bulk-step-upload">
                    <div className="flex items-start gap-2">
                        <FileCsv className="w-6 h-6 text-indigo-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <h3 className="font-semibold text-slate-900">Bulk Employee + User Onboarding</h3>
                            <p className="text-sm text-slate-500 leading-snug">
                                Upload a CSV with up to <strong>5,000 rows</strong>. The system validates
                                everything (roles, departments, designations, reporting managers, archived
                                collisions, duplicates) before any record is written. You review the preview
                                and then explicitly Confirm to commit.
                            </p>
                        </div>
                    </div>

                    <div className="bg-indigo-50 border border-indigo-200 rounded-md px-4 py-3 text-xs text-indigo-800">
                        <strong>Modes per row:</strong> <code>combined</code> (Employee + User), <code>link_existing</code>{" "}
                        (link an existing User to an existing Employee), or <code>standalone</code> (user-only,
                        for external auditors / vendor portal). The CSV template explains every column.
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                            <Download className="w-4 h-4 mr-2" /> Download template
                        </Button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".csv,text/csv"
                            className="hidden"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="bulk-file-input"
                        />
                        <Button
                            variant="outline"
                            onClick={() => fileInputRef.current?.click()}
                            data-testid="pick-csv-btn"
                        >
                            <Upload className="w-4 h-4 mr-2" /> Pick CSV file
                        </Button>
                        {file && (
                            <>
                                <span className="text-sm text-slate-700">
                                    <FileCsv className="w-4 h-4 inline mr-1" /> {file.name} ({(file.size / 1024).toFixed(1)} KB)
                                </span>
                                <Button onClick={() => validate(file)} disabled={validating} data-testid="validate-csv-btn">
                                    {validating ? "Validating..." : "Validate"}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Step 2 — Validation preview */}
            {batch && !result && (
                <div className="space-y-4" data-testid="bulk-step-preview">
                    <div className="bg-white border border-slate-200 rounded-lg p-5">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div>
                                <h3 className="font-semibold text-slate-900">Validation preview</h3>
                                <p className="text-xs text-slate-500">
                                    Batch <code>{batch.batch_id}</code> · expires{" "}
                                    <Clock className="w-3 h-3 inline" /> {new Date(batch.expires_at).toLocaleString()}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button variant="outline" size="sm" onClick={reset} data-testid="bulk-reset-btn">
                                    <ArrowLeft className="w-4 h-4 mr-1" /> Pick different file
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => validate(file)}
                                    disabled={validating}
                                    data-testid="bulk-revalidate-btn"
                                >
                                    <ArrowsClockwise className="w-4 h-4 mr-1" /> Re-validate
                                </Button>
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
                            <Stat label="Total rows" value={batch.summary.total_rows} />
                            <Stat label="OK" value={batch.summary.ok} color="emerald" />
                            <Stat label="Collision" value={batch.summary.warn_collision} color="amber" />
                            <Stat label="Error" value={batch.summary.error} color="rose" />
                            <Stat label="Ready?" value={batch.summary.ready_to_execute ? "Yes" : "No"} color={batch.summary.ready_to_execute ? "emerald" : "slate"} />
                        </div>
                    </div>

                    {/* Per-row table */}
                    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="bulk-preview-table">
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-3 py-2 reedos-eyebrow">#</th>
                                        <th className="px-3 py-2 reedos-eyebrow">Status</th>
                                        <th className="px-3 py-2 reedos-eyebrow">Mode</th>
                                        <th className="px-3 py-2 reedos-eyebrow">Identifier</th>
                                        <th className="px-3 py-2 reedos-eyebrow">Issues / Notes</th>
                                        <th className="px-3 py-2 reedos-eyebrow">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {batch.rows.map((r) => (
                                        <tr key={r.row_number} className={r.classification === "error" ? "bg-rose-50/30" : r.classification === "warn_collision" ? "bg-amber-50/30" : ""} data-testid={`bulk-row-${r.row_number}`}>
                                            <td className="px-3 py-2 text-slate-500 font-mono text-xs">{r.row_number}</td>
                                            <td className="px-3 py-2"><ClassPill classification={r.classification} /></td>
                                            <td className="px-3 py-2 text-xs"><code>{r.mode || "—"}</code></td>
                                            <td className="px-3 py-2 text-xs">
                                                <div className="font-medium text-slate-800 break-all">
                                                    {r.raw?.email || r.raw?.existing_employee_code || "—"}
                                                </div>
                                                <div className="text-slate-500">{r.raw?.name || r.raw?.existing_user_email || ""}</div>
                                            </td>
                                            <td className="px-3 py-2 text-xs text-slate-700">
                                                {r.errors && r.errors.length > 0 && (
                                                    <ul className="text-rose-700 list-disc list-inside">
                                                        {r.errors.map((e, i) => <li key={i}>{e}</li>)}
                                                    </ul>
                                                )}
                                                {r.warnings && r.warnings.length > 0 && (
                                                    <ul className="text-amber-700 list-disc list-inside">
                                                        {r.warnings.map((w, i) => <li key={i}>{w}</li>)}
                                                    </ul>
                                                )}
                                                {r.archived_match && (
                                                    <div className="bg-amber-100 border border-amber-200 rounded px-2 py-1 mt-1 text-amber-900">
                                                        Archived: <strong>{r.archived_match.employee_code}</strong> {r.archived_match.full_name}{" "}
                                                        ({r.archived_match.department || "—"} · {r.archived_match.designation || "—"})
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {r.classification === "warn_collision" ? (
                                                    <CollisionPicker row={r} onChange={onCollisionChange} disabled={executing} />
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Action bar */}
                    <div className="sticky bottom-0 bg-white border border-slate-200 rounded-lg px-5 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="bulk-actions-bar">
                        <div className="text-xs text-slate-600">
                            {unresolvedCollisions.length > 0 ? (
                                <span className="text-amber-700">
                                    ⚠️ {unresolvedCollisions.length} collision row(s) need an action picked.
                                </span>
                            ) : batch.summary.ready_to_execute ? (
                                <span className="text-emerald-700">✓ Batch is ready to commit.</span>
                            ) : (
                                <span className="text-rose-700">
                                    ✗ {batch.summary.error} error row(s) — fix the CSV and re-validate.
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <Label className="text-xs">Mode:</Label>
                            <select
                                className="text-xs h-8 px-2 border border-slate-200 rounded-md bg-white"
                                value={executeMode}
                                onChange={(e) => setExecuteMode(e.target.value)}
                                data-testid="bulk-exec-mode-select"
                            >
                                <option value="fail_fast">fail-fast (atomic)</option>
                                <option value="continue">continue (per-row)</option>
                            </select>
                            <Button
                                onClick={() => setShowConfirm(true)}
                                disabled={!batch.summary.ready_to_execute || unresolvedCollisions.length > 0 || executing}
                                data-testid="bulk-commit-btn"
                            >
                                <FloppyDisk className="w-4 h-4 mr-2" /> Commit batch
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Step 3 — Result */}
            {result && (
                <div className="bg-white border border-slate-200 rounded-lg p-6 space-y-4" data-testid="bulk-step-result">
                    {result.halted ? (
                        <>
                            <div className="flex items-center gap-2 text-rose-700">
                                <XCircle className="w-6 h-6" />
                                <h3 className="font-semibold">Batch halted — fully rolled back</h3>
                            </div>
                            <p className="text-sm text-slate-700">{result.detail}</p>
                            <div className="bg-rose-50 border border-rose-200 rounded-md p-3 text-xs">
                                <strong>Rollback summary:</strong>{" "}
                                {JSON.stringify(result.rolled_back)} · Rows committed before halt:{" "}
                                {result.succeeded_before_halt.join(", ") || "none"}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-2">
                                <CheckCircle className="w-6 h-6 text-emerald-600" />
                                <h3 className="font-semibold text-slate-900">Batch committed</h3>
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                <Stat label="Succeeded" value={result.succeeded_count} color="emerald" />
                                <Stat label="Failed" value={result.failed_count} color={result.failed_count > 0 ? "rose" : "slate"} />
                                <Stat label="Mode" value={result.mode} />
                                <Stat label="Duration" value={`${((new Date(result.completed_at) - new Date(result.started_at)) / 1000).toFixed(1)}s`} />
                            </div>
                            {result.failed_count > 0 && (
                                <div className="bg-rose-50 border border-rose-200 rounded-md p-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-rose-800">
                                            {result.failed_count} row(s) failed. Download the failure report:
                                        </span>
                                        <Button size="sm" variant="outline" onClick={downloadFailures} data-testid="bulk-download-failures-btn">
                                            <Download className="w-4 h-4 mr-1" /> failures.csv
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div className="flex items-center gap-2 pt-3 border-t border-slate-200">
                        <Button variant="outline" onClick={reset} data-testid="bulk-result-reset-btn">
                            <ArrowsCounterClockwise className="w-4 h-4 mr-2" /> Process another CSV
                        </Button>
                    </div>
                </div>
            )}

            {/* Confirm modal */}
            <Dialog open={showConfirm} onOpenChange={(o) => !o && !executing && setShowConfirm(false)}>
                <DialogContent className="max-w-md" data-testid="bulk-confirm-modal">
                    <DialogHeader>
                        <DialogTitle>Commit batch</DialogTitle>
                        <DialogDescription>
                            Ready to commit <strong>{batch?.summary?.ok + batch?.summary?.warn_collision}</strong> row(s)
                            in <code>{executeMode}</code> mode. This action will create/modify Employee + User records.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-sm space-y-1">
                        <div><strong>Batch:</strong> <code>{batch?.batch_id}</code></div>
                        <div><strong>File:</strong> {batch?.csv_filename || "—"}</div>
                        <div>
                            <strong>Mode:</strong>{" "}
                            {executeMode === "fail_fast"
                                ? "fail-fast — first error halts entire batch & rolls back."
                                : "continue — each row commits independently; failures collected in failures.csv."}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={executing} data-testid="bulk-confirm-cancel-btn">
                            Cancel
                        </Button>
                        <Button onClick={execute} disabled={executing} data-testid="bulk-confirm-execute-btn">
                            <FloppyDisk className="w-4 h-4 mr-2" />
                            {executing ? "Committing..." : "Commit batch"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}


function Stat({ label, value, color = "slate" }) {
    const palette = {
        slate: "bg-slate-50 border-slate-200 text-slate-700",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
        rose: "bg-rose-50 border-rose-200 text-rose-700",
        amber: "bg-amber-50 border-amber-200 text-amber-700",
    };
    return (
        <div className={`border rounded-md px-3 py-2 ${palette[color]}`}>
            <div className="text-2xl font-bold">{value}</div>
            <div className="text-[11px] uppercase tracking-wider">{label}</div>
        </div>
    );
}
